import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { ToastTypes } from '@shared/models/toast';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-file-upload',
	imports: [CommonModule, FormsModule, ReactiveFormsModule],
	templateUrl: './file-upload.component.html',
	styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent<T extends { [K in keyof T]: AbstractControl }> {
	@Input() formGroup!: FormGroup<T>;
	@Input() name!: Extract<keyof T, string | number>;
	@Input() multiple: boolean = false;
	@Input() allowedTypes: string[] = [];
	@Input() disabled: boolean = false;

	private toastService = inject(ToastService);

	isDragging = false;

	get control(): AbstractControl<File[]> {
		return this.formGroup.controls[this.name];
	}

	get files(): File[] {
		return this.control.value || [];
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files) return;

		this.addFiles(Array.from(input.files));
	}

	get isNative(): boolean {
		return Capacitor.isNativePlatform();
	}

	async openCamera() {
		try {
			const image = await Camera.getPhoto({
				quality: 100,
				allowEditing: false,
				resultType: CameraResultType.Base64,
				source: CameraSource.Camera,
			});

			if (image.base64String) {
				const fileName = `photo_${new Date().getTime()}.jpeg`;
				const file = this.base64ToFile(image.base64String, fileName, 'image/jpeg');
				this.addFiles([file]);
			}
		} catch (error) {
			console.error('Camera error:', error);
			this.toastService.showToast({
				title: 'Camera Error',
				message: 'Could not access the camera.',
				type: ToastTypes.ERROR,
			});
		}
	}

	base64ToFile(base64: string, fileName: string, mimeType: string): File {
		const byteCharacters = atob(base64);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += 512) {
			const slice = byteCharacters.slice(offset, offset + 512);
			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		const blob = new Blob(byteArrays, { type: mimeType });
		return new File([blob], fileName, { type: mimeType });
	}

	onDrop(event: DragEvent) {
		event.preventDefault();
		this.isDragging = false;
		if (event.dataTransfer?.files) {
			this.addFiles(Array.from(event.dataTransfer.files));
		}
	}

	addFiles(newFiles: File[]) {
		const validFiles = newFiles.filter((file) => this.allowedTypes.length === 0 || this.allowedTypes.includes(file.type));
		const invalidFiles = newFiles.filter((file) => this.allowedTypes.length > 0 && !this.allowedTypes.includes(file.type));

		if (invalidFiles.length > 0) {
			this.toastService.showToast({
				title: 'File Upload Error',
				message: `Some files are not allowed: ${invalidFiles.map((f) => f.name).join(', ')}`,
				type: ToastTypes.ERROR,
			});
		}

		this.control.setValue(this.multiple ? [...this.files, ...validFiles] : validFiles);
		this.control.markAsTouched();
		this.control.markAsDirty();
	}

	removeFile(index: number) {
		const updatedFiles = [...this.files];
		updatedFiles.splice(index, 1);
		this.control.setValue(updatedFiles);
		if (this.control.value.length === 0) {
			this.control.markAsPristine();
		}
	}

	preventDefaults(event: Event) {
		event.preventDefault();
	}

	getFileURL(file: File): string {
		return URL.createObjectURL(file);
	}
}
